import React, { useState, useEffect, Fragment } from 'react';
import { Label, Checkbox } from '@rebass/forms';
import { Flex, Text, Box, Button } from 'rebass/styled-components';
import FadeLoader from 'react-spinners/FadeLoader';
import theme from '../../theme';
import styled from 'styled-components';
import scrollTo from 'gatsby-plugin-smoothscroll';
import InputBox from '../components/InputBox';
import InputPhone from '../components/InputPhone';
import InputDate from '../components/InputDate';
import OptionBox from '../components/OptionBox';
import InputFilterBox from '../components/InputFilterBox';
import ProgressBar from '../components/ProgressBar';
import VerificationCode from '../components/VerificationCode';
import Document from '../components/Document';
import Message from '../components/Message';
import { validateEmail } from '../resources/utils';
import { STORAGE_KEYS } from '../resources/constants';
import {
  sendCodeService,
  verifyCodeService,
  signUpService,
  generateDocIdService,
  updateSignedConsentService,
  trackStatsService
} from '../services/services';
import { QUESTIONS } from '../resources/constants';
import encryptData from '../resources/encryptor';
import { trackCustomEvent } from 'gatsby-plugin-google-analytics';

const Question = styled(Text)`
  font-size: 24px;
  font-weight: bold;
  line-height: 1.5;
`;

const Number = styled(Text)`
  font-size: 16px;
  color: ${props => props.theme.colors.lilac};
  margin-right: 5px;
`;

const Description = styled(Text)`
  font-size: 20px;
  line-height: 1.5;
  color: ${props => props.theme.colors.grey2};
`;

const TextConfirm = styled(Text)`
  font-size: 16px;
  line-height: 1.5;
  color: ${props => props.theme.colors.grey4};
  padding: 0 0 20px 0;
`;

const TextCheckbox = styled(Text)`
  font-size: 12px;
  font-weight: bold;
  line-height: 1.5;
  color: ${props => props.theme.colors.black};
`;

let appointmentScheduledQuestions = ["visitDate"];

const SignUpForm = ( {setStep} ) => {
  const [current, setCurrent] = useState('');
  const [questions, setQuestions] = useState([]);
  const [hideQuestions, setHideQuestions] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [
    calculatePercentageMultipleChoice,
    setCalculatePercentageMultipleChoice
  ] = useState(false);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [nameMessage, setNameMessage] = useState('');
  const [patientId, setPatientId] = useState('');
  const [docId, setDocId] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [disableResendCode, setDisableResendCode] = useState(true);
  const [disableSend, setDisableSend] = useState(true);
  const [sending, setSending] = useState(false);
  const [showBar, setShowBar] = useState(true);
  const [letterOption, setLetterOption] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitWithoutCode, setSubmitWithoutCode] = useState(false);
  const [valuesForm, setValuesForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    streetAddress: '',
    apartmentNumber: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: '',
    clinicianName: '',
    clinicianType: '',
    clinicianCity: '',
    clinicianState: '',
    appointmentScheduled: '',
    visitDate: '',
    healthInsurancePlan: '',
    phoneType: ''
  });

  useEffect(() => {
    if (questions.length == 0){
      setQuestions(QUESTIONS);
      setCurrent("firstName");
    } 
  }, []);

  useEffect(() => {
    if (calculatePercentageMultipleChoice) {
      calculatePercentage();
      setCalculatePercentageMultipleChoice(false);
    }
  }, [valuesForm, calculatePercentageMultipleChoice]);

  useEffect(() => {
    if ((current !== "") && (current !== "verifyCode")){
        if (current === "verifyPhone")
          setCurrent("phoneVerificationCell1");
        else
          document.getElementById(current).focus()
    }
  }, [current]);

  useEffect(() => {
    let option = '';
    if (((current === "clinicianType")
      || (current === "appointmentScheduled")
      || (current === "healthInsurancePlan")
      || (current === "phoneType")) && (letterOption !== '')){
        let question = questions.find(q => q.name === current);
        option = question.options.find(o => o.id === letterOption);
        if (option)
          setOptionBox(option.text, current, question.next)
    }
  }, [current, letterOption]);

  document.addEventListener('keydown', handleKeyOption);
  function handleKeyOption(e) {
    if (e.key !== undefined){
      let letter = e.key.toUpperCase();
      setLetterOption(letter);
    }
  }
  useEffect(() => {
    if (valuesForm['appointmentScheduled'] === 'No'){
      setHideQuestions(appointmentScheduledQuestions);
      if (current === 'appointmentScheduled')
        moveTo("healthInsurancePlan");
    }
    else 
      setHideQuestions([]);
  }, [valuesForm, current]);

  useEffect(() => {
    if (code.length == 6) {
      setDisableSubmit(false);
      setCurrent('submit');
    } else {
      setMessage('');
      setDisableSubmit(true);
    }
  }, [code]);

  useEffect(() => {
    if (valuesForm.phoneNumber != '') setDisableResendCode(false);
    else setDisableResendCode(true);

  }, [valuesForm]);

  const handleSubmitWithoutCode = () => {
    setCurrent('submitWithoutCode');
    setDisableSubmit(false);
    setSubmitWithoutCode(submitWithoutCode => !submitWithoutCode);
  };

  const changeFormHandler = e => {
    setMessage('');
    setValuesForm({ ...valuesForm, [e.target.name]: e.target.value });
  };

  const keyPressHandler = (e, buttonId, name) => {
    setMessage('');
    setCurrent(name);
    if (e.which === 13 || e.keyCode === 13 || e.key === 'Enter')
      document.getElementById(buttonId).click();
  };

  const calculatePercentage = () => {
    let totalQuestions = Object.keys(valuesForm).length;
    let answered = 0;
    Object.entries(valuesForm).forEach(([key, value]) => {
      if (value){
        if (!((valuesForm['appointmentScheduled'] === 'No') && appointmentScheduledQuestions.includes(key)))
          answered++;
      }
    });

    if (valuesForm['appointmentScheduled'] === 'Yes' && (valuesForm['visitDate'] === '' && document.getElementById('visitDate').value !== '')) 
      answered++;

    if (valuesForm['appointmentScheduled'] === 'No') 
      totalQuestions = totalQuestions - 1;

    let p = ((answered * 100) / totalQuestions).toFixed();
    setPercentage(p);
  };

  const handleTrackCustomEvent = step => {
    trackCustomEvent({
      category: 'form',
      action: step
    });
  };

  const moveTo = id => {
    const step = id.substr(2);
    handleTrackCustomEvent(step);
    
    setTimeout(function() {
      scrollTo('#q_' + id);
    }, 500);

    if (id !== 'doc'){
      setTimeout(function() {
        setLetterOption("");
        setCurrent(id);
      }, 1000);
    }
  };

  const next = (next, name, type, value, required) => {
    setMessage('');
    if (validateForm(name, type, value, required)) {
      calculatePercentage();
      if (name != 'phoneNumber') {
        moveTo(next);
      } else {
        sendCode('verifyCode', next, 1);
      }
    }
  };

  const setOptionBox = (text, name, next) => {
    setValuesForm({ ...valuesForm, [name]: text });
    setCalculatePercentageMultipleChoice(true);
    moveTo(next);
  };

  const sendCode = async (name, next, n) => {
    setMessage('');
    const data = {
      phoneNumber: valuesForm.phoneNumber
    };
    try{
      let response = await sendCodeService(data);
      if (response.status == '200') {
        if (n == 1) {
          moveTo(next);
          setTimeout(function() {
            setCurrent('phoneVerificationCell1');
          }, 1000);
        }
        setMessage(response.data.message);
        setNameMessage(name);
      }
    }catch(error) {
      setMessage('What is your cell phone number?');
      setNameMessage(name);
    }
  };


  const validateForm = (name, type, value, required) => {
    if (required && (value === '' || value == null) && type !== 'date') {
      moveTo(name);
      let message = 'Please fill this in';
      if (type == ''){
        message = 'Please select an option';
      }
      setMessage(message);
      setNameMessage(name);
      return false;
    }

    if (type === 'email' && value !== '' && !validateEmail(value)) {
      moveTo(name);
      setMessage('Please enter a valid email');
      setNameMessage(name);
      return false;
    }

    if (required && type === 'date' && document.getElementById(name).value == '') {
      moveTo(name);
      setMessage('Please enter a date');
      setNameMessage(name);
      return false;
    }
    return true;
  };

  const validForm = () => {
    for (let i = 0; i < questions.length; i++) {
      if (
        !validateForm(
          questions[i].name,
          questions[i].type,
          valuesForm[questions[i].name],
          questions[i].required
        )
      )
        return false;
    }
    return true;
  };

  const handleSubmit = async (name) => {
    setMessage('');
    if (validForm()) {
      setDisableSubmit(true);
      const data = {
        event: 'SUBMIT'
      };

      try{
        let response = await trackStatsService(data);
        if (response.status == "200"){
          if (!submitWithoutCode) {
            const dataVerifyCode = {
              phoneNumber: valuesForm.phoneNumber,
              code: code
            };
            try{
              let verifyCodeResponse = await verifyCodeService(dataVerifyCode);
              if (verifyCodeResponse.status == '200') {
                const data = {
                  event: 'VERIFY_PHONE'
                };
                try{
                  let trackStatsResponse = await trackStatsService(data);
                  if (trackStatsResponse.status == '200') {
                    prepareDoc(true);
                  }
                }catch(error){
                  console.log(error);
                };
              }
            }catch(error){
              setMessage('Please check code');
              setNameMessage(name);
              setDisableSubmit(false);
            };
          } 
          else 
            prepareDoc(false)
        }
      }catch(error){
          console.log(error);
      }
    }
  };

  const prepareDoc = async (verifyPhoneNumber) => {
    setLoading(true);
    setShowBar(false);
    moveTo("doc");

    const params = { ...valuesForm };
    params['adid'] = localStorage.getItem(STORAGE_KEYS.ADID);
    params['adgroup'] = localStorage.getItem(STORAGE_KEYS.ADGROUP);
    params['phoneVerified'] = verifyPhoneNumber;
    if (params.visitDate == '') delete params.visitDate;

    try{
      let response = await encryptData(params);
      const dataSignUp = {
        message: response
      };
      let signUpResponse = await signUpService(dataSignUp);
      if (signUpResponse.status == "200"){
        setPatientId(signUpResponse.data.patientId);
        const dataDoc = {
          templateId: process.env.TEMPLATE_ID,
          email: valuesForm.email,
          firstName: valuesForm.firstName,
          lastName: valuesForm.lastName
        };
        let generateDocResponse = await generateDocIdService(dataDoc);
        if (generateDocResponse.status == "200"){
          setLoading(false);
          setDocId(generateDocResponse.data.documentLink);
        }
      }

    }catch(error){
      console.log(error);
    };
  };

  const send = async () => {
    setDisableSend(true);
    setSending(true);
    const dataUpdateSigned = {
      patientId: patientId
    };
    try {
      let response = await updateSignedConsentService(dataUpdateSigned);
      if (response.status == '200'){
        setStep("thanks");
      }
    } catch(error){
      console.log(error);
    }
  };

  if (!questions) return null;
  return (
    <Fragment>
      <Flex
        flexDirection="column"
        justifyContent="center"
        px={[7, 16, 18]}
        mb={[3, 0]}
        backgroundColor="white"
      >
        <Box>
          {questions &&
            questions.map((element, index) => (
              <Fragment key={index}>
                <Box
                  tabIndex={index}
                  id={'q_' + element.name}
                  pt={16}
                  pb={19}
                  sx={{
                    display: (hideQuestions.includes(element.name)) ? 'none' : 'block'
                  }}
                >
                  <Flex alignItems="flex-start" ml={-5} mb={5}>
                    <Number pt={2} pr={1}>
                      {element.id}
                    </Number>
                    <Question>
                      {element.question.replace(
                        '...',
                        valuesForm.clinicianName
                      )}{' '}
                      {element.required ? '*' : ''}
                    </Question>
                  </Flex>
                  {element.description && (
                    <Box pb={5}>
                      {element.description.map((d, index) => (
                        <Fragment key={index}>
                          <Description>{d}</Description>
                        </Fragment>
                      ))}
                    </Box>
                  )}

                  {element.questionType == 'input' && (
                    <Fragment>
                      {element.name && (
                        <InputBox
                          name={element.name}
                          type={element.type}
                          value={valuesForm[element.name]}
                          setValue={changeFormHandler}
                          keyPressHandler={keyPressHandler}
                          placeholder={element.placeholder}
                          required={element.required}
                        />
                      )}
                      {message && nameMessage == element.name && (
                        <Message message={message} />
                      )}
                      <Button
                        id={'b_' + element.name}
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          next(
                            element.next,
                            element.name,
                            element.type,
                            valuesForm[element.name],
                            element.required
                          )
                        }
                      >
                        OK
                      </Button>
                    </Fragment>
                  )}

                  {element.questionType == 'inputPhone' && (
                    <Fragment>
                      <InputPhone
                        name={element.name}
                        valuesForm={valuesForm}
                        setValuesForm={setValuesForm}
                        keyPressHandler={keyPressHandler}
                        placeholder={element.placeholder}
                        required={element.required}
                      />
                      {message && nameMessage == element.name && (
                        <Message message={message} />
                      )}
                      <Button
                        id={'b_' + element.name}
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          next(
                            element.next,
                            element.name,
                            element.type,
                            valuesForm[element.name],
                            element.required
                          )
                        }
                      >
                        OK
                      </Button>
                    </Fragment>
                  )}

                  {element.questionType == 'inputDate' && (
                    <Fragment>
                      <InputDate
                        name={element.name}
                        keyPressHandler={keyPressHandler}
                        placeholder={element.placeholder}
                        required={element.required}
                      />
                      {message && nameMessage == element.name && (
                        <Message message={message} />
                      )}
                      <Button
                        id={'b_' + element.name}
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setValuesForm({
                            ...valuesForm,
                            [element.name]: document.getElementById(
                              element.name
                            ).value
                          });
                          next(
                            element.next,
                            element.name,
                            element.type,
                            valuesForm[element.name],
                            element.required
                          );
                        }}
                      >
                        OK
                      </Button>
                    </Fragment>
                  )}

                  {element.questionType == 'text' && (
                    <Fragment>
                      {element.id == 'f' && (
                        <TextConfirm>
                          {valuesForm.streetAddress}{' '}
                          {valuesForm.apartmentNumber}
                          <br />
                          {valuesForm.city}, {valuesForm.state}
                          <br />
                          {valuesForm.zipCode}
                        </TextConfirm>
                      )}
                      <Button
                        id={element.name}
                        type="button"
                        variant="secondary"
                        width={element.id == 'f' ? '140px' : '90px'}
                        onClick={() => 
                          moveTo(element.next)
                        }
                      >
                        {element.id == 'f' ? 'CONTINUE' : 'OK'}
                      </Button>
                      {element.id == 'f' && (
                        <Button
                          ml={5}
                          width="90px"
                          type="button"
                          variant="link"
                          onClick={() => 
                            moveTo('streetAddress')
                          }
                        >
                          EDIT
                        </Button>
                      )}
                    </Fragment>
                  )}

                  {element.questionType == 'inputFilter' && (
                    <Fragment>
                      <InputFilterBox
                        name={element.name}
                        placeholder={element.placeholder}
                        valuesForm={valuesForm}
                        setValuesForm={setValuesForm}
                        options={element.options}
                        keyPressHandler={keyPressHandler}
                      />
                      {message && nameMessage == element.name && (
                        <Message message={message} />
                      )}
                      <Button
                        id={'b_' + element.name}
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          next(
                            element.next,
                            element.name,
                            element.type,
                            valuesForm[element.name],
                            element.required
                          )
                        }
                      >
                        OK
                      </Button>
                    </Fragment>
                  )}

                  {element.questionType == 'verifyCode' && (
                    <Fragment>
                      <VerificationCode
                        setCode={setCode}
                        current={current}
                        setCurrent={setCurrent}
                      />
                      {message && nameMessage == element.name && (
                        <Message message={message}></Message>
                      )}
                      <Flex justifyContent="center" alignItems="center" my={4}>
                        <Label>
                          <Box mr={1}>
                            <Checkbox
                              id="submitWithoutCode"
                              name="submitWithoutCode"
                              onClick={handleSubmitWithoutCode}
                              color="#726DE1"
                            />
                          </Box>
                          <Box>
                            <TextCheckbox my={1}>
                              I DIDN'T GET A CODE
                            </TextCheckbox>
                          </Box>
                        </Label>
                      </Flex>
                      <Flex>
                        <Button
                          id="submit"
                          type="button"
                          disabled={disableSubmit}
                          variant="secondary"
                          width="100px"
                          onClick={() => handleSubmit(element.name)}
                        >
                          Submit
                        </Button>
                        <Button
                          type="button"
                          ml={5}
                          disabled={disableResendCode}
                          variant="link"
                          width="150px"
                          onClick={() =>
                            sendCode('verifyCode', element.next, 2)
                          }
                        >
                          RESEND CODE
                        </Button>
                      </Flex>
                    </Fragment>
                  )}


                  {element.questionType == 'multipleChoice' && (
                    <Box id={element.name} tabIndex={index}>
                      {element.options.map((option, i) => (
                        <Fragment key={i}>
                          <OptionBox
                            letter={option.id}
                            text={option.text}
                            isActive={valuesForm[element.name] == option.text}
                            name={element.name}
                            next={option.next}
                            setOptionBox={setOptionBox}
                          ></OptionBox>
                        </Fragment>
                      ))}

                      {message && nameMessage == element.name && (
                        <Message message={message} />
                      )}
                    </Box>
                  )}

                </Box>
              </Fragment>
            ))}

          <Box id="q_doc" pt={12} pb={19}>
            {loading && (
              <Flex
                justifyContent="center"
                alignContent="center"
                alignItems="center"
                my={5}
              >
                <FadeLoader
                  height={15}
                  width={5}
                  radius={2}
                  color={theme.colors.main}
                  loading="true"
                />
              </Flex>
            )}
            {docId && (
              <Fragment>
                <Document
                  docId={docId}
                  setDisableSend={setDisableSend}
                  next="b_send"
                />
                <Button
                  id="b_send"
                  type="button"
                  variant="secondary"
                  onClick={() => send()}
                  disabled={disableSend}
                  my={4}
                >
                  Send
                </Button>
              </Fragment>
            )}

            {sending && (
              <Flex
              justifyContent="center"
              alignContent="center"
              alignItems="center"
              my={5}
              >
                <FadeLoader
                  height={15}
                  width={5}
                  radius={2}
                  color={theme.colors.main}
                  loading="true"
                />
              </Flex>
            )}

          </Box>
        </Box>
      </Flex>

      {showBar && (
        <Flex
          justifyContent="flex-end"
          alignItems="flex-end"
          width="100%"
          sx={{
            position: 'fixed',
            bottom: '0',
            zIndex: '1'
          }}
          py={4}
          px={6}
        >
          <ProgressBar
            bgcolor="#898989"
            value={percentage}
            width={[1, 1 / 2, 1 / 3]}
          ></ProgressBar>
        </Flex>
      )}
    </Fragment>
  );
};

export default SignUpForm;
